import React from "react";
import { Link } from "react-router-dom";
import { CheckIcon, RobotIcon, SparkleIcon } from "@phosphor-icons/react";
import { usePageView } from "@/lib/usePageView";
import HubFooter from "./HubFooter";
import StoreBackground from "./StoreBackground";
import StoreTopbar from "./StoreTopbar";
import { useSalesOpen } from "./LaunchNotify";
import { RiskNotice } from "./RiskNotice";

// MT5 software-licence plans. Prices unchanged; cTrader Copy pricing hidden for the software-only launch.
const TIERS = [
  {
    id: "single",
    name: "Single bot",
    price: 49,
    strap: "A software licence for any one listed bot.",
    features: [
      "Licence for 1 bot of your choice (swap once per billing cycle)",
      "Protected .ex5 download + personal licence key",
      "Use on 1 demo + 1 live MT5 account of your own",
      "Cancel any time",
    ],
  },
  {
    id: "five",
    name: "Five bots",
    price: 149,
    strap: "Software licences for any five listed bots.",
    features: [
      "Licences for any 5 bots (swap once per billing cycle)",
      "Protected .ex5 downloads + personal licence key",
      "Each bot usable on 1 demo + 1 live MT5 account of your own",
      "Cancel any time",
    ],
    save: "£96 less than 5 × Single",
  },
  {
    id: "all",
    name: "Everything",
    price: 249,
    strap: "Software licences for every listed bot.",
    features: [
      "Licences for every bot in the store",
      "Newly listed bots become available to download",
      "Each bot usable on 1 demo + 1 live MT5 account of your own",
      "Cancel any time",
    ],
  },
];

const SMALL_PRINT = [
  ["Renewal & cancellation", "Plans renew monthly on the same date until you cancel. Cancel any time from Manage billing on your account page - access continues until the end of the period you've paid for, and you won't be charged again."],
  ["No performance fees", "You pay a flat software-licence fee. It doesn't change with trading results, and we never receive any share of your account."],
  ["What you're buying", "A licence to download and use software. It is not investment advice, a managed account or a trading service. See the risk, terms & refunds page."],
];

export default function Pricing() {
  const salesOpen = useSalesOpen();
  usePageView("/pricing");
  const primary = "#38BDF8";
  const secondary = "#FBBF24";

  return (
    <StoreBackground theme="mt5">
      <div data-testid="pricing-page" className="relative min-h-screen bg-transparent text-[#F1F5F9] overflow-x-clip">
        <StoreTopbar subtitle="Pricing" />

        <section className="relative z-10 max-w-[1200px] mx-auto px-6 pt-16 pb-8">
          <div className="mono text-[11px] uppercase tracking-[0.3em]" style={{ color: primary }}>
            MetaTrader 5 software licences
          </div>
          <h1 className="text-4xl md:text-5xl font-medium tracking-tight mt-3 max-w-3xl leading-[1.05]">
            One flat monthly price. <span style={{ color: secondary }}>No performance fees.</span>
          </h1>
          <p className="text-base text-[#94A3B8] mt-4 max-w-2xl">
            Each plan is a monthly licence to download and use a set number of MT5 Expert Advisors on your own MetaTrader 5
            accounts. You choose which bots to license, and you install and run them yourself.
          </p>
          {salesOpen === false && (
            <div data-testid="pricing-prelaunch" className="mt-6 inline-block mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border border-amber-500/40 bg-amber-500/10 text-[#FBBF24]">
              Pre-launch · subscriptions are not open yet
            </div>
          )}
        </section>

        <section className="relative z-10 max-w-[1200px] mx-auto px-6 pb-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {TIERS.map((t, i) => {
              const c = i === 1 ? primary : secondary;
              return (
                <div key={t.id} data-testid={`tier-${t.id}`} className="relative rounded-xl border p-6 flex flex-col"
                  style={{ borderColor: c, background: `${c}0A`, backdropFilter: "blur(8px)" }}>
                  <div className="mono text-[10px] uppercase tracking-widest" style={{ color: c }}>{t.name}</div>
                  <div className="mt-3 flex items-baseline gap-1">
                    <span className="text-4xl font-medium tracking-tight">£{t.price}</span>
                    <span className="text-sm text-[#94A3B8] mono">/mo</span>
                  </div>
                  {t.save && <div className="mono text-[10px] mt-1" style={{ color: c }}>{t.save}</div>}
                  <p className="text-sm text-[#CBD5E1] mt-3 leading-relaxed">{t.strap}</p>
                  <ul className="mt-5 space-y-2.5 text-sm flex-1">
                    {t.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-[#E5E7EB]">
                        <CheckIcon size={16} weight="bold" className="mt-0.5 flex-shrink-0" style={{ color: c }} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to={salesOpen ? `/account?plan=${t.id}` : `/notify?plan=${t.id}`} data-testid={`tier-${t.id}-cta`}
                    className="mt-6 w-full text-center mono text-[11px] uppercase tracking-widest px-4 py-3 rounded-lg transition-transform font-semibold hover:scale-[1.02]"
                    style={{ background: c, color: "#050608" }}>
                    {salesOpen ? "Choose plan" : "Notify me at launch"}
                  </Link>
                </div>
              );
            })}
          </div>

          <RiskNotice testid="pricing-risk" className="mt-10" />

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6 text-sm text-[#94A3B8]">
            {SMALL_PRINT.map(([h, b]) => (
              <div key={h}>
                <div className="mono text-[10px] uppercase tracking-widest text-[#F1F5F9] mb-2">
                  <SparkleIcon size={12} className="inline mr-1" style={{ color: primary }} />{h}
                </div>
                <p>{b}</p>
              </div>
            ))}
          </div>

          <div className="mt-14 rounded-xl border p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4"
            style={{ borderColor: `${primary}40`, background: `${primary}08` }}>
            <div>
              <div className="mono text-[10px] uppercase tracking-widest" style={{ color: primary }}>
                <RobotIcon className="inline mr-1" size={12} weight="fill" />MetaTrader 5
              </div>
              <h3 className="text-2xl font-medium mt-1">Check the requirements first</h3>
              <p className="text-sm text-[#CBD5E1] mt-2 max-w-2xl">
                You'll need MetaTrader 5, an MT5 account with a broker of your choice, and a computer or VPS that stays on while
                the software runs. The setup guide explains installation and the licence check step by step.
              </p>
            </div>
            <Link to="/setup" data-testid="pricing-to-setup-cta" className="mono text-[11px] uppercase tracking-widest px-5 py-3 rounded-lg border whitespace-nowrap"
              style={{ borderColor: primary, color: primary }}>
              Read the setup guide →
            </Link>
          </div>
        </section>

        <HubFooter />
      </div>
    </StoreBackground>
  );
}
